import { useFormContext } from 'react-hook-form'
import { useMemo } from 'react'
import Select from 'components/FormUtils/Select'
import { TextInput } from 'components/FormUtils/TextInput'
import { ActionRenderer } from './ActionRenderer'
import apiService from 'services/api'

const triggerOptions = [
  { value: 'deal_created', label: 'Deal created' },
  { value: 'field_changed_account_owner', label: 'Account owner changed' },
  { value: 'field_changed_sales_stage', label: 'Sales stage changed' },
  { value: 'stage_duration', label: "A deal's sales stage" },
  { value: 'days_since_reply', label: 'Days since reply' },
  { value: 'days_since_sent', label: 'Days since sent' },
  { value: 'days_since_company_updated', label: 'Days since company updated' },
  { value: 'days_since_deal_updated', label: 'Days since deal updated' },
]

const comparisonOperatorOptions = [
  { value: 'greater_than', label: 'Greater than (>)' },
  { value: 'greater_than_equal', label: 'Greater than or equal to (≥)' },
  { value: 'less_than', label: 'Less than (<)' },
  { value: 'less_than_equal', label: 'Less than or equal to (≤)' },
  { value: 'equal', label: 'Equal (=)' },
]

const durationUnitOptions = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
]

// Component for "Account Owner Changed" trigger - includes condition + TO + user
function AccountOwnerChangedTrigger() {
  const { control } = useFormContext()
  const api = apiService()
  const { data: users } = api.useGetUsers()

  const userOptions = useMemo(() => {
    if (!users) return []
    return users.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
    }))
  }, [users])

  return (
    <div className="flex gap-1 w-full items-center">
      <Select
        name="trigger_type"
        control={control}
        placeholder="Choose condition..."
        options={triggerOptions}
        className="flex-1"
      />
      <div className="flex items-center justify-center px-3 py-2 mt-3 bg-gray-100 rounded text-sm font-medium text-gray-700">
        TO
      </div>
      <Select
        name="trigger_target_user_id"
        control={control}
        placeholder="Choose user..."
        options={userOptions}
        className="flex-1"
      />
    </div>
  )
}

// Component for "Sales Stage Changed" trigger - includes condition + TO + stage
function SalesStageChangedTrigger() {
  const { control } = useFormContext()
  const api = apiService()
  const { data: salesStageOptions } = api.useGetSalesStageOptions()

  return (
    <div className="flex gap-1 w-full items-center">
      <Select
        name="trigger_type"
        control={control}
        placeholder="Choose condition..."
        options={triggerOptions}
        className="flex-1"
      />
      <div className="flex items-center justify-center px-3 py-2 mt-3 bg-gray-100 rounded text-sm font-medium text-gray-700">
        TO
      </div>
      <Select
        name="trigger_target_stage_id"
        control={control}
        placeholder="Choose sales stage..."
        options={salesStageOptions}
        className="flex-1"
      />
    </div>
  )
}

// Component for stage duration trigger - includes trigger + HAS BEEN + stage + FOR + value + unit
function StageDurationTrigger() {
  const { control } = useFormContext()
  const api = apiService()
  const { data: salesStageOptions } = api.useGetSalesStageOptions()

  return (
    <div className="flex gap-1 w-full items-center">
      {/* Trigger type dropdown */}
      <Select
        name="trigger_type"
        control={control}
        placeholder="Choose condition..."
        options={triggerOptions}
        className="flex-1"
      />

      {/* HAS BEEN label */}
      <div className="flex items-center justify-center px-4 py-3 mt-3 bg-gray-100 rounded-lg text-sm font-medium text-gray-700">
        HAS BEEN
      </div>

      {/* Stage selector */}
      <Select
        name="trigger_target_stage_id"
        control={control}
        placeholder="Choose stage..."
        options={salesStageOptions}
        className="flex-1"
      />

      {/* FOR label */}
      <div className="flex items-center justify-center px-4 py-3 mt-3 bg-gray-100 rounded-lg text-sm font-medium text-gray-700">
        FOR
      </div>

      {/* Value + unit */}
      <div className="flex gap-1 items-center">
        <div className="w-24 shrink-0 mt-3">
          <TextInput
            name="duration_value"
            control={control}
            placeholder="Value"
            type="number"
            className="w-full h-[38px]"
            noMargin
          />
        </div>

        <Select
          name="duration_unit"
          control={control}
          placeholder="Unit"
          options={durationUnitOptions}
          className="w-32"
        />
      </div>
    </div>
  )
}

// Component for time-based triggers (days since) - includes trigger + IS + comparison operator + value
function TimeBasedTrigger() {
  const { control } = useFormContext()

  return (
    <div className="flex gap-1 w-full items-center">
      {/* Trigger type dropdown */}
      <Select
        name="trigger_type"
        control={control}
        placeholder="Choose condition..."
        options={triggerOptions}
        className="flex-1"
      />

      {/* IS label */}
      <div className="flex items-center justify-center px-4 py-3 mt-3 bg-gray-100 rounded-lg text-sm font-medium text-gray-700">
        IS
      </div>

      {/* Comparison operator + value wrapper */}
      <div className="flex gap-1 flex-1 items-center">
        <Select
          name="comparison_operator"
          control={control}
          placeholder="Choose comparison..."
          options={comparisonOperatorOptions}
          className="flex-1"
        />

        <div className="w-24 shrink-0 mt-3">
          <TextInput
            name="duration_value"
            control={control}
            placeholder="Value"
            type="number"
            className="w-full h-[38px]"
            noMargin
          />
        </div>
      </div>
    </div>
  )
}

// Default trigger dropdown when no specific trigger requires additional fields
function DefaultTriggerSelect() {
  const { control } = useFormContext()

  return (
    <Select
      name="trigger_type"
      control={control}
      placeholder="Choose condition..."
      options={triggerOptions}
      className="w-full"
    />
  )
}

// Component mapping for trigger types
const TRIGGER_COMPONENT_MAP: Record<string, React.ComponentType> = {
  stage_duration: StageDurationTrigger,
  days_since_reply: TimeBasedTrigger,
  days_since_sent: TimeBasedTrigger,
  days_since_company_updated: TimeBasedTrigger,
  days_since_deal_updated: TimeBasedTrigger,
  field_changed_account_owner: AccountOwnerChangedTrigger,
  field_changed_sales_stage: SalesStageChangedTrigger,
}

// Trigger renderer that switches based on trigger type
function TriggerRenderer({
  selectedTrigger,
}: {
  selectedTrigger: string | null
}) {
  if (!selectedTrigger) {
    return <DefaultTriggerSelect />
  }

  const Component = TRIGGER_COMPONENT_MAP[selectedTrigger]
  return Component ? <Component /> : <DefaultTriggerSelect />
}

interface TriggerActionStepProps {
  automationId?: number
}

export function TriggerActionStep({ automationId }: TriggerActionStepProps) {
  const { watch } = useFormContext()

  const selectedAction = watch('action_type')
  const selectedTrigger = watch('trigger_type')

  return (
    <div className="space-y-6">
      <div className="bg-surface-muted rounded-xl p-6 space-y-6 [&_.form-control-select]:!bg-white">
        {/* IF Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="font-medium text-gray-700">IF</span>
          </div>

          <TriggerRenderer selectedTrigger={selectedTrigger} />
        </div>

        {/* Divider */}
        <div className="border-t border-line-subtle"></div>

        {/* THEN Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="font-medium text-gray-700">THEN</span>
          </div>

          <div className="flex gap-1 w-full items-center">
            <ActionRenderer
              selectedAction={selectedAction}
              selectedTrigger={selectedTrigger}
              automationId={automationId}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
